import { CountVizData, CountVizDatum } from "../../types";
import { LABEL_CSS_CLASSES } from "../_base_barchart_ui_configs";
import { formatPercentage } from "../comps/CustomizedLabel";

/**
 * Measures the actual pixel width of text by rendering it in a temporary SVG element
 * This is more accurate than character-count estimation
 * 
 * @param text - The text to measure
 * @param cssClass - CSS class from LABEL_CSS_CLASSES constant
 */
const measureTextWidth = (text: string, cssClass: string): number => {
  // Create a temporary SVG element
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.style.position = "absolute";
  svg.style.visibility = "hidden";
  svg.style.width = "0";
  svg.style.height = "0";
  
  const textElement = document.createElementNS("http://www.w3.org/2000/svg", "text");
  textElement.textContent = text;
  textElement.setAttribute("class", cssClass);
  
  svg.appendChild(textElement);
  document.body.appendChild(svg);
  
  // Get the actual width
  const bbox = textElement.getBBox();
  const width = bbox.width;
  
  // Clean up
  document.body.removeChild(svg);
  
  return width;
};

/**
 * Formats a value as it would appear in the chart (with percent sign or as count)
 */
const formatValueForDisplay = (
  count: number,
  percentage: number,
  showPercent: boolean,
  isStacked: boolean = false
): string => {
  if (showPercent) {
    return formatPercentage(percentage);
  }
  return count.toString();
};

interface CalculateLabelWidthsParams {
  data: CountVizData;
  isBreakdown: boolean;
  breakdownDisplayType?: "grouped" | "stacked";
  showPercent: boolean;
  showCount: boolean;
  totalDatasetCount: number;
}

interface LabelWidths {
  maxPercentWidth: number;
  maxCountWidth: number;
}

/**
 * Calculates the maximum widths needed for percent, count, and label elements
 * across all bars in the chart to ensure consistent alignment
 */
export const calculateLabelWidths = ({
  data,
  isBreakdown,
  breakdownDisplayType,
  showPercent,
  showCount,
  totalDatasetCount,
}: CalculateLabelWidthsParams): LabelWidths => {
  let maxPercentWidth = 0;
  let maxCountWidth = 0;

  // Helper to process a single datum
  const processDatum = (datum: CountVizDatum, shouldCalculateCountWidth: boolean = false) => {
    const { count, percentage } = datum.labels;
    
    // Calculate percent/value width
    const valueText = formatValueForDisplay(count, percentage, showPercent);
    const valueWidth = measureTextWidth(valueText, LABEL_CSS_CLASSES.PERCENT_VALUE);
    maxPercentWidth = Math.max(maxPercentWidth, valueWidth);
    
    // Calculate count width - only when:
    // 1. showPercent is true (otherwise count is already the main value)
    // 2. showCount is true (user wants to show count)
    // 3. shouldCalculateCountWidth is true (for this chart type)
    if (shouldCalculateCountWidth && showPercent && showCount) {
      const countText = count.toString();
      const countWidth = measureTextWidth(countText, LABEL_CSS_CLASSES.COUNT_VALUE);
      maxCountWidth = Math.max(maxCountWidth, countWidth);
    }
  };

  // Process data based on chart type
  if (!isBreakdown) {
    // Single value charts - calculate count width (will only be used if showPercent && showCount)
    (data as CountVizDatum[]).forEach((datum) => processDatum(datum, true));
  } else if (breakdownDisplayType === "grouped") {
    // Grouped charts - process each bar individually with count width calculation
    (data as CountVizDatum[][]).forEach((group) => {
      group.forEach((datum) => {
        processDatum(datum, true); // Calculate count width for grouped bars
      });
      
      // Also consider the group label itself for percent width calculation
      if (group.length > 0) {
        // Calculate group total for percent display
        const totalCount = group.reduce((sum, d) => sum + d.labels.count, 0);
        const groupPercentage = (totalCount / totalDatasetCount) * 100;
        const groupValueText = formatValueForDisplay(totalCount, groupPercentage, showPercent);
        const groupValueWidth = measureTextWidth(groupValueText, LABEL_CSS_CLASSES.PERCENT_VALUE);
        maxPercentWidth = Math.max(maxPercentWidth, groupValueWidth);
      }
    });
  } else if (breakdownDisplayType === "stacked") {
    // Stacked charts - calculate count width if both showPercent and showCount are true
    (data as CountVizDatum[][]).forEach((group) => {
      // Calculate stacked bar total
      const totalCount = group
        .filter(seg => seg.labels.barColor !== "transparent" && seg.labels.count > 0)
        .reduce((sum, d) => sum + d.labels.count, 0);
      
      const groupPercentage = (totalCount / totalDatasetCount) * 100;
      const stackedValueText = formatValueForDisplay(totalCount, groupPercentage, showPercent, true);
      const stackedValueWidth = measureTextWidth(stackedValueText, LABEL_CSS_CLASSES.STACKED_LABEL);
      maxPercentWidth = Math.max(maxPercentWidth, stackedValueWidth);
      
      // Calculate count width for stacked if both flags are true
      if (showPercent && showCount) {
        const countText = totalCount.toString();
        const countWidth = measureTextWidth(countText, LABEL_CSS_CLASSES.STACKED_LABEL);
        maxCountWidth = Math.max(maxCountWidth, countWidth);
      }
    });
  }

  // generous padding to account for rendering differences and ensure no overlap
  // The measured width is exact, but we need extra space for:
  // 1. Browser rendering variations
  // 2. Font smoothing/anti-aliasing
  // 3. Safety margin
  const padding = 1.15;
  
  return {
    maxPercentWidth: Math.ceil(maxPercentWidth * padding),
    maxCountWidth: Math.ceil(maxCountWidth * padding),
  };
};

